import React, { useEffect, useState } from "react";
import {
  Layout,
  Spacing,
  Title,
  Paragraph,
  WithFeedbackProps,
  withFeedback,
} from "@sicredi/react";

import "./index.scss";
import CanalAtendimento from "../../components/CanalAtendimento";
import ListaDPOs from "../../components/ListaDPOs";

export const PoliticaPrivacidadePage: React.FC<WithFeedbackProps> = ({
  setError,
  setSucceeded,
  setException,
  clearFeedback,
}) => {
  const [linkPoliticaCookies, setLinkPoliticaCookies] = useState<
    undefined | JSX.Element
  >();

  const hasPoliticaCookies = () => {
    const link: string | undefined = document
      .getElementById("politica-privacidade-root")
      ?.getAttribute("politica-cookies-link")
      ?.toString();

    const politicaCookies =
      link === undefined ? undefined : (
        <Paragraph className="paragrafo">
          Para saber mais sobre cookies, confira a Política específica em{" "}
          <a
            href={link}
            target="_blank"
            className="highlight-green"
            rel="noreferrer"
          >
            {link}.
          </a>
        </Paragraph>
      );

    setLinkPoliticaCookies(politicaCookies);
  };

  useEffect(() => hasPoliticaCookies(), []);

  return (
    <div>
      <Layout className="main-layout">
        <Layout.Container className="layout-container">
          <p className="up-first-title">Política de privacidade</p>

          <Title size="large" className="first-title">
            Confidencialidade, Disponibilidade e Integridade
          </Title>
          <p className="sub-first-title">
            Nossa Política de Privacidade enfatiza o nosso compromisso e
            respeito em relação à segurança e privacidade das informações dos
            nossos usuários.
          </p>

          <Spacing appearance="xx-large"></Spacing>
          <Spacing appearance="xx-large"></Spacing>

          <div className="politica-privacidade-subsection">
            <Title size="xx-large" className="subsection-title">
              Nossa política
            </Title>

            <div className="subsection-text">
              <Spacing appearance="xx-large"></Spacing>

              <Paragraph className="paragrafo">
                A nova Lei Geral de Proteção de Dados - LGPD (Lei 13.709/2018)
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Para o pleno exercício de suas atividades, o Sicredi —
                diretamente ou através de seus representantes, colaboradores ou
                fornecedores — precisa realizar inúmeras operações de tratamento
                de dados pessoais. O Sicredi está comprometido a assegurar que
                essas operações de tratamento de dados estejam alinhadas às
                melhores práticas de mercado e às exigências legais.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Assim, com o objetivo de sinalizar esse compromisso
                institucional com o direito fundamental à privacidade e sua
                materialização no direito à proteção de dados pessoais ,
                considerando a entrada em vigor da Lei no 13.709/18 (Lei Geral
                de Proteção de Dados, "LGPD") , o Sicredi apresenta, abaixo, sua
                "Política de Privacidade - Privacy Statement" ("Statement").
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Essa Política busca demonstrar, de modo claro, fácil e
                transparente:
              </Paragraph>

              <ul>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    os tipos de dados pessoais que nós coletamos sobre você;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    como nós tratamos esses dados e para quais finalidades;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    com quem os seus dados podem ser compartilhados;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    quais as medidas de segurança adotadas para a sua proteção;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    e os seus direitos como titular de dados.
                  </span>
                </li>
              </ul>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Quais tipos de dados pessoais o Sicredi trata em suas
                  atividades?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                O tratamento de seus dados pessoais pode ser essencial para o
                exercício das atividades do Sicredi, como para permitir a sua
                navegação nesse website , viabilizar a aquisição de produtos
                e/ou a prestação de serviços contratados por você, aprimorar a
                experiência no Shopping Sicredi (“Marketplace”) e manter o
                relacionamento com você.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Para se associar à nossa Cooperativa, por exemplo, você
                necessariamente terá de compartilhar alguns dados conosco. As
                operações financeiras, como aplicações, transferências e
                quaisquer outras realizadas pelos associados via Sicredi
                Internet são reconhecidas desde já, cada qual, como um Ato
                Cooperativo e estarão vinculadas às disposições legais que
                regulam o cooperativismo, ao Regimento Interno do Sicredi, ao
                estatuto social da Cooperativa de Crédito do associado e às
                deliberações das assembleias e do conselho de administração
                desta – aos quais você livre e espontaneamente adere no momento
                da associação.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Confira, abaixo, alguns exemplos de dados pessoais que podem ser
                coletados diretamente pelo Sicredi junto a você para finalidades
                específicas:
              </Paragraph>

              <ul>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      dados básicos de identificação:
                    </span>{" "}
                    nome, idade, profissão, nome da mãe, gênero, data e local de
                    nascimento, para a finalidade de realização de seu cadastro,
                    formalização de sua associação ao sistema de crédito
                    cooperativo e/ou contratação de um produto/serviço a seu
                    pedido;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">detalhes de contato:</span>{" "}
                    e-mail, número de telefone, endereço, para a finalidade de
                    manutenção de nosso relacionamento com você;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      documentos de identificação pessoal:
                    </span>{" "}
                    número de RG, CPF, passaporte, para confirmar a sua
                    identidade e garantir a sua adequada identificação e
                    qualificação;&nbsp;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      dados de registros eletrônicos e de interação em
                      plataformas:
                    </span>{" "}
                    informações obtidas através do uso de ferramentas de
                    Analytics ou cookies (necessários e não necessários) nos
                    sites de domínio Sicredi, como endereço IP, ID de
                    dispositivos móveis, data e hora de acesso, preferências
                    registradas, produtos e categorias pesquisados ou
                    visualizados e funcionalidades utilizadas, para garantir o
                    funcionamento adequado e seguro de nossas plataformas, bem
                    como para aprimorar a sua experiência ao utilizá-las;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">dados de pagamento:</span>{" "}
                    endereço de entrega, endereço de cobrança e informações de
                    cartão de crédito, quando escolhido como forma de pagamento
                    no Shopping Sicredi, para viabilizar a compra de produtos a
                    seu pedido;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">dados de geolocalização:</span>{" "}
                    informações geográficas, para indicar as agências, agentes
                    credenciados ou mesmo caixas do Banco24horas ou Saque e
                    Pague mais próximos de você, para exibir anúncios
                    personalizados conforme a sua proximidade com certos
                    produtos e serviços, assim como para fins de prevenção à
                    fraude e garantia da segurança de suas operações em nosso
                    Marketplace;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">dados de compras:</span>{" "}
                    informações coletadas automaticamente para análise e
                    monitoramento de suas compras em nosso Marketplace, com a
                    finalidade de garantir a segurança de suas transações: tipo
                    de produto, quantidade, valor da mercadoria (unitário),
                    valor total da compra ou transação, natureza da transação
                    financeira;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">dados de pesquisas:</span>{" "}
                    informações e opiniões fornecidas voluntariamente por você
                    ao participar de pesquisas conduzidas pelo Sicredi;&nbsp;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">credenciais de acesso:</span>{" "}
                    login e senha, para a finalidade de permitir a sua navegação
                    segura no canal de internet banking, no Shopping Sicredi
                    e/ou em nossos aplicativos; aplicativos;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      informações profissionais:
                    </span>{" "}
                    currículo, experiências profissionais, histórico acadêmico e
                    outras informações colhidas em formulários de candidatura a
                    vagas abertas no Sicredi via “Trabalhe Conosco”;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      informações fornecidas voluntariamente por você:
                    </span>{" "}
                    demais dados fornecidos por você em comunicação conosco
                    pelos nossos canais disponíveis via “Fale Conosco”, como por
                    e-mail, telefonemas, WhatsApp corporativo e canal de
                    denúncias, caso você deseje se identificar, assim como por
                    interações em redes sociais ou comentários de avaliações de
                    produtos.
                  </span>
                </li>
              </ul>

              {linkPoliticaCookies}

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Adicionalmente, veja, abaixo, alguns exemplos de informações
                coletadas em outras fontes, tais como:
              </Paragraph>

              <ul>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">Open Finance e Pix:</span>{" "}
                    informações como contas bancárias em outras instituições
                    financeiras ou meios de pagamento, através do Sistema
                    Bancário Aberto (“Open Finance”) e Pagamentos Instantâneos
                    (“Pix”), de acordo com o seu consentimento;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      Fontes para Prevenção à Lavagem de Dinheiro e ao
                      Financiamento do Terrorismo:
                    </span>{" "}
                    informações obtidas para prevenir fraudes e combater lavagem
                    de dinheiro e ao financiamento ao terrorismo, de acordo com
                    as regulamentações de órgãos como o Banco Central do Brasil,
                    incluindo dados contidos em listas de alertas, como OFAC,
                    CSNU e outras internacionais;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      Informações manifestamente públicas:
                    </span>{" "}
                    informações tornadas manifestamente públicas por você,
                    respeitados os seus direitos e observados os princípios da
                    LGPD;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">Bureaus de Crédito:</span>{" "}
                    informações constantes no SPC – Brasil, Serasa Experian e
                    Quod, para a proteção do crédito.
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      Sistema de Informações de Crédito:
                    </span>{" "}
                    informações disponíveis no Sistema de Informações de Crédito
                    (“SCR”) mantido pelo Banco Central do Brasil, quando você
                    autorizar especificamente a coleta para a atuação
                    responsável do Sicredi na quantificação de riscos e
                    concessão de crédito, conforme termos da Resolução CMN n.º
                    5.037, de 29 de setembro de 2022.
                  </span>
                </li>
              </ul>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                O Sicredi tratará os seus dados pessoais de modo seguro e
                responsável , inclusive de seus dados financeiros (saques,
                depósitos, aportes, pagamentos, recebimentos, transferências de
                recursos, operações no mercado de câmbio) e comportamentais
                (perfil de consumo e crédito que permitem a diferenciação entre
                transações normais e suspeitas) obtidos no escopo de sua
                associação/contratação, de acordo com a LGPD , a Lei do Sigilo
                Bancário (Lei Complementar n.º 105/2001) e o Código de Defesa do
                Consumidor (Lei n.º 8.078/1990).
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  O Sicredi trata dados pessoais sensíveis?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Um dado pessoal sensível é aquele que revela informações sobre
                origem racial ou étnica , convicção religiosa, opinião política,
                filiação a sindicato ou a organização de caráter religioso ,
                filosófico ou político, dado referente à saúde ou à vida sexual,
                dado genético ou biométrico, quando vinculado a uma pessoa
                natural. Em determinadas situações , o Sicredi necessitará
                tratar dados pessoais sensíveis para finalidades legítimas,
                como, por exemplo:
              </Paragraph>

              <ul>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      Selfies e dados biométricos:
                    </span>{" "}
                    a contratação de alguns de nossos produtos depende do upload
                    de uma selfie (foto pessoal) e/ou dados biométricos
                    necessários para prevenir fraudes, autenticar suas
                    informações e garantir a sua segurança em nosso ambiente
                    digital. Igualmente, para a sua associação à Cooperativa e
                    participação em assembleias, a coleta de biometria pode ser
                    necessária para confirmar a sua identidade e validar a sua
                    entrada nos eventos, de modo seguro.
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    <span className="bold-text">
                      Declarações de exposição política:
                    </span>{" "}
                    para o devido cumprimento de normas do Banco Central do
                    Brasil visando à prevenção dos crimes de “lavagem” de
                    dinheiro e ocultação de bens, o Sicredi necessita instaurar
                    procedimentos para conhecer os seus clientes - o que inclui
                    o tratamento de informações de Pessoa Exposta Politicamente
                    (“PEP”), conforme a Circular n.º 3.978/2020.
                  </span>
                </li>
              </ul>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  O Sicredi trata dados pessoais de crianças e/ou adolescentes?
                </span>
              </Paragraph>

              <Paragraph className="paragrafo">
                O Sicredi trata dados pessoais de menores de idade que, por meio
                de seu(s) pai(s) ou representante(s) legal(is), cadastrem-se,
                associem-se e/ou contratem produtos ou serviços do Sicredi, tais
                como o SicredInvest Criança. Em todos os casos, o tratamento de
                dados pessoais é realizado no melhor interesse da criança e/ou
                do adolescente, de acordo com as hipóteses legais da lei.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Os dados pessoais coletados de menores de 12 (doze) anos de
                idade são aqueles estritamente necessários ao cadastro e/ou à
                relação contratual celebrada com a instituição, tais como
                aqueles mencionados na seção acima: dados básicos de
                identificação (nome, idade, nome do pai ou do representante
                legal, gênero, data e local de nascimento), detalhes de contato
                (e-mail, número de telefone, endereço), documentos de
                identificação pessoal (certidão de nascimento, número de RG,
                CPF, passaporte), dentre outros dados necessários aos processos
                operacionais e estratégicos envolvidos a partir do processo de
                associação, abertura de conta e/ou contratação de produto ou
                serviço. Em regra, crianças não participam de pesquisas
                conduzidas pelo Sicredi.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Os dados pessoais coletados de maiores de 12 (doze) anos de
                idade, mas menores de 18 (dezoito) anos de idade, podem incluir,
                também, informações profissionais (currículo, experiências
                profissionais, histórico acadêmico) de adolescentes que se
                candidatem às nossas vagas de Jovem Aprendiz.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Quais são os fundamentos para o tratamento de seus dados
                  pessoais?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Qualquer tratamento de dados realizado pelo Sicredi estará
                baseado em pelo menos um fundamento legal e se dará de forma
                adequada com a finalidade da sua coleta e de acordo com os
                princípios da boa-fé , finalidade, adequação, necessidade, livre
                acesso, qualidade de dados, segurança, prevenção, não
                discriminação e, é claro, transparência
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                O tratamento de seus dados pessoais ocorrerá especialmente (i)
                para cumprimento de obrigações legais ou regulatórias; (ii) para
                exercício regular de direitos em processos ou contratos; (iii)
                quando necessários para execução de contrato firmado com você ou
                para os procedimentos necessários para a sua celebração; (iv)
                para proteção de crédito; ou (v) para atender a interesses
                legítimos do Sicredi, de seus parceiros ou terceiros. Em outros
                casos, o tratamento de dados pessoais será condicionado ao seu
                consentimento.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Como tratamos os seus dados pessoais e para quais finalidades?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                O tratamento de seus dados pessoais poderá atender às seguintes
                finalidades:
              </Paragraph>

              <ul>
                <li className="lista-marcador">
                  <span className="lista-texto">associá-lo à cooperativa;</span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    confirmar sua identidade e endereço;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    melhorar sua experiência como cliente Sicredi, através do
                    desenvolvimento de novas funcionalidades e implementação de
                    melhorias em nossas atividades;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    prestar os serviços e produtos oferecidos de forma
                    eficiente;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">garantir a sua segurança;</span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    enviar produtos adquiridos, comunicações de interesse,
                    atualizações de pedidos, informações de entrega ou ações de
                    marketing;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    oferecer produtos e serviços que o beneficiem, sendo seu
                    direito optar por não receber tais informações;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    atender às suas solicitações pelos nossos canais de
                    atendimento;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    garantir a sua adequada identificação e qualificação, de
                    acordo com a legislação vigente;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    colaborar e/ou contribuir com ordem judicial ou requisição
                    por autoridade administrativa, fiscal ou policial;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    construir banco de dados para efetuar estudos estatísticos
                    para formação de base de conhecimento;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    realizar investigações internas para prevenir fraudes,
                    ilícitos, crimes financeiros e crimes de lavagem de dinheiro
                    e/ou de financiamento ao terrorismo, inclusive em cooperação
                    com autoridades competentes para fins de segurança pública e
                    investigação/repressão de ilícitos;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    assegurar o funcionamento adequado e seguro do nosso
                    Marketplace;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    auxiliar no diagnóstico e solução de problemas técnicos;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    garantir o cumprimento de obrigação legal ou regulatória a
                    qual o Sicredi esteja submetido;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    exercer direitos em processos judiciais, administrativos
                    e/ou arbitrais.
                  </span>
                </li>
              </ul>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                O Sicredi, como instituição financeira autorizada a funcionar
                pelo Banco Central do Brasil, tem o dever de garantir que as
                operações financeiras que ocorram em seu domínio sejam seguras e
                livre de vícios. Para tanto, é importante assegurar a
                identificação e qualificação de nossos clientes através de
                consultas a bancos de dados de caráter público e privado, de
                acordo com a legislação vigente. Informações sobre operações que
                envolvam recursos provenientes de qualquer prática criminosa
                serão devidamente comunicadas às autoridades competentes, sem
                configuração da quebra de sigilo bancário e/ou de violação à
                LGPD.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Com quem podemos compartilhar os seus dados?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                O compartilhamento de seus dados pessoais ocorrerá
                excepcionalmente, no escopo das atividades listadas acima, como
                para fins de: (i) cumprimento de leis e/ou regulações de órgãos
                reguladores, como o Banco Central do Brasil; (ii) fornecimento
                de produtos contratados por você, como seguros ofertados em
                parceria com seguradoras; (iii) prestação de serviços que o
                beneficiem, respeitada a sua legítima expectativa; (iv)
                colaboração com autoridades competentes, de acordo com a
                legislação vigente; ou (v) cumprimento de ordens, proteção de
                interesses e exercício regular de direitos em processo judicial,
                administrativo ou arbitral. Isso significa que o
                compartilhamento de seus dados poderá ser feito com entidades
                públicas e autoridades competentes, assim como empresas e
                instituições parceiras, tal como fornecedores de tecnologia da
                informação que dão suporte aos nossos sistemas - os quais
                igualmente se submetem a compromissos e normas de proteção de
                dados. Um bom exemplo disso é o compartilhamento de seus dados
                com a bandeira de seu cartão, desta forma viabilizando que você
                colha os benefícios disto, como descontos em lojas e pagamento a
                prazo.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Em atendimento à Resolução Conjunta n.º 06/2023 do Banco
                  Central do Brasil e do Conselho Monetário Nacional, dados e
                  informações sobre indícios de ocorrências ou tentativas de
                  fraudes serão compartilhados com outras instituições
                  financeiras, instituições de pagamento e demais autorizadas a
                  funcionar pelo Banco Central do Brasil, com a finalidade de
                  subsidiar procedimentos e controles para prevenção de fraudes
                  no âmbito do Sistema Financeiro Nacional. Ao iniciar ou manter
                  um relacionamento com o Sicredi, os associados ficam cientes
                  da operação e reconhecem que tal compartilhamento de dados não
                  constitui quebra de sigilo bancário ou violação à LGPD.
                </span>
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">O que é proteção de crédito?</span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Nós, no Sicredi, lidamos com riscos financeiros, e, para
                suportá-los, devemos estar preparados e adotar alguns mecanismos
                legais. No Brasil, existem bancos de dados de proteção ao
                crédito, como SPC - Brasil e Serasa, que fornecem as informações
                necessárias para que nós possamos iniciar e estreitar nossa
                relação negocial, seja para empréstimo, ou abertura de conta
                corrente, por exemplo.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                O Sicredi poderá ainda comunicar aos gestores dos bancos de
                proteção ao crédito o eventual descumprimento de qualquer
                obrigação sua ou o atraso de pagamento, bem como fornecer aos
                gestores do Cadastro Positivo, autorizados a funcionar no Banco
                do Brasil, seus dados financeiros e de pagamento relativos a
                operações de crédito e obrigações de pagamento adimplidas ou em
                andamento.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Por quanto tempo ficamos em posse de seus dados?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Nós manteremos os seus dados, pelo menos, pelo tempo que você
                possuir vínculo conosco. Após o encerramento deste período
                inicial de retenção, nós manteremos os seus dados apenas pelos
                prazos que forem necessários para cumprir obrigações
                regulatórias e a legislação vigente. Nestes casos, mesmo se você
                desejar excluir seus dados pessoais, poderemos conservá-los por
                exigência legal ou regulatória.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Que precauções foram implementadas para proteger os seus
                  dados?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Adotamos medidas de segurança para o tratamento de todos os
                dados pessoais coletados, sobretudo os dados pessoais sensíveis,
                como mecanismos de criptografia de ponta a ponta. Essas
                providências, técnicas e organizacionais, foram implementadas
                para prevenir o acesso não autorizado e situações acidentais ou
                ilícitas de perda, destruição, alteração, comunicação ou difusão
                de seus dados pessoais.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Avaliamos periodicamente os procedimentos de segurança adotados,
                a fim de garantir a privacidade das informações. As informações
                coletadas via Site Corporativo do Sicredi são armazenadas no
                Sistema de Informações do Sicredi, que conta com mecanismos para
                garantir a integridade, disponibilidade e confidencialidade de
                seus dados, que são manipulados somente por pessoas autorizadas
                e para fins preestabelecidos pelo Sicredi, de acordo com padrões
                rígidos de segurança. Além disso, as informações não são
                fornecidas ou divulgadas a terceiros, exceto nos casos descritos
                nesta Política de Privacidade. É importante ressaltar que o
                Sicredi não envia e-mails sem a sua autorização ou legítima
                expectativa em recebê-los. No Site Corporativo do Sicredi, o
                usuário pode conhecer o histórico da instituição, os produtos e
                serviços oferecidos, bem como obter informações gerais, sem
                precisar fornecer nenhum dado pessoal.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Pode ocorrer transferência internacional de dados pessoais?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Excepcionalmente, os seus dados pessoais poderão ser enviados
                para localidade fora do Brasil, a depender do tipo de relação
                entre você e o Sicredi. Por exemplo, numa operação de câmbio com
                o exterior. Quando isso ocorrer, a transferência ocorrerá em
                observância das bases legais e dos requisitos estabelecidos na
                legislação aplicável, em observância à privacidade e proteção de
                seus dados.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  Quais são os seus direitos como titular desses dados?
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                O Sicredi garantirá a você, titular de dados pessoais, o
                exercício gratuito e facilitado dos direitos previstos na lei
                aplicável, dentre eles:
              </Paragraph>

              <ul>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    confirmação da existência de tratamento de seus dados
                    pessoais;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">acesso a esses dados;</span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    correção de dados incompletos, inexatos ou desatualizados;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    anonimização, bloqueio ou eliminação de dados tratados com o
                    seu consentimento, quando aplicável, ou quando
                    comprovadamente desnecessários, excessivos ou tratados em
                    desconformidade com a LGPD;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    portabilidade de seus dados pessoais, mediante requisição
                    expressa;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    obtenção de informações sobre entidades públicas e privadas
                    com os quais o Sicredi compartilha os seus dados;
                  </span>
                </li>
                <li className="lista-marcador">
                  <span className="lista-texto">
                    revogação do consentimento, quando aplicável.
                  </span>
                </li>
              </ul>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                As solicitações serão submetidas a uma prévia avaliação da sua
                identidade e da viabilidade do atendimento, no formato
                solicitado, a fim de proteger o segredo comercial e/ou cumprir
                com eventuais obrigações que impeçam o completo acolhimento das
                requisições.
              </Paragraph>
              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Alguns desses direitos ainda precisam ser detalhados e
                regulamentados pela Autoridade Nacional de Proteção de Dados
                ANPD para que possam ser adequadamente atendidos pelo Sicredi.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">Alterações da Política</span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Como a privacidade e a proteção de dados é um tema dinâmico,
                poderemos modificar a presente Política a qualquer momento.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                Estamos sempre buscando melhorar nossos Produtos e Serviços, de
                modo que esta Política de Privacidade pode passar por
                atualizações para refletir as melhorias realizadas. Desta forma,
                recomendamos a visita periódica desta página para que você tenha
                conhecimento sobre as modificações efetivadas.
              </Paragraph>

              <Spacing appearance="medium"></Spacing>

              <Paragraph className="paragrafo">
                <span className="bold-text">
                  {" "}
                  Contato do Encarregado do Tratamento de Dados Pessoais do
                  Centro Administrativo Sicredi (CAS)
                </span>
              </Paragraph>
              <Paragraph className="paragrafo">
                Para mais informações sobre o tratamento dos seus dados pessoais
                ou sobre termos deste Statement, envie e-mail para o nosso
                Encarregado do tratamento de dados pessoais (DPO) do Centro
                Administrativo Sicredi (CAS):{" "}
                <span className="highlight-green">
                  encarregado_cas@sicredi.com.br.
                </span>
              </Paragraph>

              <Spacing appearance="medium"></Spacing>
            </div>
          </div>
        </Layout.Container>
      </Layout>

      <Spacing appearance="large"></Spacing>

      <Spacing appearance="large"></Spacing>

      <ListaDPOs />

      <CanalAtendimento />

      <Spacing appearance="xxx-large"></Spacing>
    </div>
  );
};

export default withFeedback(PoliticaPrivacidadePage);
